import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faCheckCircle, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import {
  fetchPromptDefinitions,
  createPromptDefinition,
  fetchBatches,
  fetchVocabulary,
  fetchPromptRuns,
  fetchPromptRun,
  triggerPromptRun,
} from "./skillsProfileApi";
import "./skillsProfile.css";

const POLL_INTERVAL_MS = 2000;
const ACTIVE_STATUSES = new Set(["pending", "running"]);

// Mirrors src/common/env.py Settings.skills_profile_{default,max}_cost_ceiling_usd.
// Not exposed by any endpoint today, so the FE keeps its own copy; the backend
// clamps to the true max regardless of what is sent here.
const DEFAULT_COST_CEILING_USD = 5.0;
const MAX_COST_CEILING_USD = 25.0;

// Only models present in the backend price table (src/evaluator/pilot/pricing.py)
// are accepted by POST /prompts, and each one's provider must match
// detect_provider(model) (src/apiserver/utils/llm_clients.py).
const PROVIDER_MODELS = [
  {
    value: "google",
    label: "Google (Vertex Gemini)",
    models: [
      { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
      { value: "gemini-3.1-pro-preview", label: "Gemini 3.1 Pro (preview)" },
      { value: "gemini-3.6-flash", label: "Gemini 3.6 Flash" },
      { value: "gemini-3.8-flash", label: "Gemini 3.8 Flash" },
      { value: "gemini-4-argon", label: "Gemini 4 Argon" },
    ],
  },
  {
    value: "anthropic",
    label: "Anthropic",
    models: [
      { value: "claude-opus-4-8", label: "Claude Opus 4.8" },
      { value: "claude-opus-4-6", label: "Claude Opus 4.6" },
    ],
  },
  {
    value: "openai",
    label: "OpenAI",
    models: [{ value: "gpt-5.1", label: "GPT-5.1" }],
  },
  {
    value: "openrouter",
    label: "OpenRouter",
    models: [{ value: "qwen/qwen3.7-plus", label: "Qwen 3.7 Plus" }],
  },
];

const REASONING_LEVELS = ["low", "medium", "high"];

const OUTPUT_CONTRACT_EXAMPLE = `{
  "tagged_quotes": [
    {
      "quote_timestamp": "MM:SS",
      "quote": "exact utterance text from the transcript",
      "skill": "Collaboration",
      "tags": ["..."],
      "rationale": "one or two sentences",
      "confidence": "high | medium | low"
    }
  ]
}`;

const blankForm = () => ({
  name: "",
  description: "",
  provider: PROVIDER_MODELS[0].value,
  model: PROVIDER_MODELS[0].models[0].value,
  temperature: 0.3,
  reasoningLevel: "medium",
  promptText: "",
});

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
};

const modelsForProvider = (provider) =>
  PROVIDER_MODELS.find((p) => p.value === provider)?.models || [];

export const TagPrompt = () => {
  const [prompts, setPrompts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [vocabulary, setVocabulary] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [savingPrompt, setSavingPrompt] = useState(false);

  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [costCeiling, setCostCeiling] = useState(DEFAULT_COST_CEILING_USD);
  const [triggering, setTriggering] = useState(false);

  const runsRef = useRef(runs);
  useEffect(() => {
    runsRef.current = runs;
  }, [runs]);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [promptsData, batchesData, vocabData, runsData] = await Promise.all([
        fetchPromptDefinitions(),
        fetchBatches(),
        fetchVocabulary().catch(() => null),
        fetchPromptRuns(),
      ]);
      setPrompts(promptsData);
      setBatches(batchesData);
      setVocabulary(vocabData);
      setRuns(runsData);
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to load Tag Prompt data: ${err?.detail || err?.message || "Unknown error"}`,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Poll active runs every 2s; stop touching a run once it reaches a terminal
  // state (completed, failed, cost_exceeded). A stale "running" row with no
  // matching run left server-side just keeps rendering as running, unchanged.
  useEffect(() => {
    const tick = async () => {
      const activeIds = runsRef.current
        .filter((r) => ACTIVE_STATUSES.has(r.status))
        .map((r) => r.id);
      if (activeIds.length === 0) return;

      const updates = await Promise.all(
        activeIds.map((id) => fetchPromptRun(id).catch(() => null))
      );

      setRuns((prev) => {
        const byId = new Map(prev.map((r) => [r.id, r]));
        updates.forEach((u) => {
          if (u) byId.set(u.id, u);
        });
        return Array.from(byId.values());
      });
    };

    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const promptMap = useMemo(() => {
    const map = new Map();
    prompts.forEach((p) => map.set(p.id, p));
    return map;
  }, [prompts]);

  const batchMap = useMemo(() => {
    const map = new Map();
    batches.forEach((b) => map.set(b.id, b));
    return map;
  }, [batches]);

  const selectedPrompt = selectedPromptId ? promptMap.get(selectedPromptId) : null;
  const isViewingExisting = !!selectedPrompt;

  const handleSelectPrompt = (prompt) => {
    setSelectedPromptId(prompt.id);
    setForm({
      name: prompt.name,
      description: prompt.description || "",
      provider: prompt.provider,
      model: prompt.model,
      temperature: prompt.temperature,
      reasoningLevel: prompt.reasoning_level || "medium",
      promptText: prompt.prompt_text,
    });
    setFeedback(null);
  };

  const handleNewPrompt = () => {
    setSelectedPromptId(null);
    setForm(blankForm());
    setFeedback(null);
  };

  const handleProviderChange = (provider) => {
    const firstModel = modelsForProvider(provider)[0]?.value || "";
    setForm((f) => ({ ...f, provider, model: firstModel }));
  };

  // React 17 pools SyntheticEvents, so `e.target.value` read lazily inside a
  // functional setState updater can come back empty; read it eagerly here.
  const updateField = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSavePrompt = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.promptText.trim()) return;

    setSavingPrompt(true);
    setFeedback(null);
    try {
      const created = await createPromptDefinition({
        name: form.name.trim(),
        description: form.description.trim() || null,
        provider: form.provider,
        model: form.model,
        temperature: Number(form.temperature),
        reasoningLevel: form.reasoningLevel || null,
        promptText: form.promptText,
      });
      setPrompts((prev) => [created, ...prev]);
      setSelectedPromptId(created.id);
      setFeedback({ type: "success", message: `Prompt "${created.name}" saved.` });
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to save prompt: ${err?.detail || err?.message || "Server error"}`,
      });
    } finally {
      setSavingPrompt(false);
    }
  };

  const toggleBatch = (batchId) => {
    setSelectedBatchIds((prev) =>
      prev.includes(batchId) ? prev.filter((id) => id !== batchId) : [...prev, batchId]
    );
  };

  const handleRunPrompt = async (e) => {
    e.preventDefault();
    if (!selectedPromptId || selectedBatchIds.length === 0) return;

    setTriggering(true);
    setFeedback(null);
    try {
      const run = await triggerPromptRun({
        promptId: selectedPromptId,
        batchIds: selectedBatchIds,
        costCeilingUsd: Number(costCeiling),
      });
      setRuns((prev) => [run, ...prev]);
      setSelectedBatchIds([]);
      setFeedback({ type: "success", message: "Run triggered." });
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to trigger run: ${err?.detail || err?.message || "Server error"}`,
      });
    } finally {
      setTriggering(false);
    }
  };

  const renderStatus = (run) => {
    switch (run.status) {
      case "pending":
        return (
          <span className="sp-run-status sp-run-queued" data-testid={`status-${run.id}`}>
            <span className="sp-run-dot" /> Queued
          </span>
        );
      case "running":
        return (
          <span className="sp-run-status sp-run-running" data-testid={`status-${run.id}`}>
            <span className="sp-run-dot" /> Running
          </span>
        );
      case "completed":
        return (
          <span className="sp-run-status sp-run-done" data-testid={`status-${run.id}`}>
            <span className="sp-run-dot" /> Completed
          </span>
        );
      case "cost_exceeded":
        return (
          <span className="sp-run-status sp-run-failed" data-testid={`status-${run.id}`}>
            <span className="sp-run-dot" /> Cost ceiling exceeded
          </span>
        );
      case "failed":
      default:
        return (
          <span className="sp-run-status sp-run-failed" data-testid={`status-${run.id}`}>
            <span className="sp-run-dot" /> Failed
          </span>
        );
    }
  };

  return (
    <div className="sp-page" data-testid="skills-profile-tag-prompt">
      <div className="sp-page-header">
        <h1 className="sp-page-title">Tag Prompt</h1>
        <p className="sp-page-sub">
          Write and run a prompt against one or more batches. Results appear back in SME Review under LLM Tags.
        </p>
      </div>

      {feedback && (
        <div className={`sp-alert sp-alert-${feedback.type}`} role="alert" data-testid="tag-prompt-feedback">
          <FontAwesomeIcon
            icon={feedback.type === "success" ? faCheckCircle : faExclamationCircle}
            style={{ marginTop: "3px" }}
          />
          <div>{feedback.message}</div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#8c959f" }} data-testid="tag-prompt-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <div style={{ marginTop: "8px" }}>Loading…</div>
        </div>
      ) : (
        <div className="sp-prompt-layout">
          <div className="sp-prompt-list" data-testid="prompt-list">
            {prompts.map((p) => (
              <div
                key={p.id}
                className={`sp-prompt-list-item${selectedPromptId === p.id ? " active" : ""}`}
                data-testid={`prompt-list-item-${p.id}`}
                onClick={() => handleSelectPrompt(p)}
              >
                <div className="name">{p.name}</div>
                <div className="meta">
                  {p.provider} · {p.model}
                </div>
              </div>
            ))}
            <div
              className="sp-prompt-list-item sp-prompt-list-new"
              data-testid="new-prompt-btn"
              onClick={handleNewPrompt}
            >
              + New prompt
            </div>
          </div>

          <div className="sp-prompt-editor">
            <form className="sp-card" onSubmit={handleSavePrompt}>
              <div className="sp-card-title">Prompt details</div>

              <div className="sp-grid-2" style={{ marginBottom: "14px" }}>
                <div className="sp-field">
                  <span className="sp-field-label">Name</span>
                  <input
                    className="sp-control"
                    data-testid="prompt-name-input"
                    value={form.name}
                    onChange={updateField("name")}
                    disabled={isViewingExisting}
                    placeholder="Collaboration Tagger v3"
                  />
                </div>
                <div className="sp-field">
                  <span className="sp-field-label">Description (optional)</span>
                  <input
                    className="sp-control"
                    data-testid="prompt-description-input"
                    value={form.description}
                    onChange={updateField("description")}
                    disabled={isViewingExisting}
                    placeholder="What this prompt looks for…"
                  />
                </div>
              </div>

              <div className="sp-grid-3" style={{ marginBottom: "14px" }}>
                <div className="sp-field">
                  <span className="sp-field-label">Provider</span>
                  <select
                    className="sp-control"
                    data-testid="prompt-provider-select"
                    value={form.provider}
                    onChange={(e) => handleProviderChange(e.target.value)}
                    disabled={isViewingExisting}
                  >
                    {PROVIDER_MODELS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sp-field">
                  <span className="sp-field-label">Model</span>
                  <select
                    className="sp-control"
                    data-testid="prompt-model-select"
                    value={form.model}
                    onChange={updateField("model")}
                    disabled={isViewingExisting}
                  >
                    {modelsForProvider(form.provider).map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sp-field">
                  <span className="sp-field-label">Reasoning level</span>
                  <select
                    className="sp-control"
                    data-testid="prompt-reasoning-select"
                    value={form.reasoningLevel}
                    onChange={updateField("reasoningLevel")}
                    disabled={isViewingExisting}
                  >
                    {REASONING_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="sp-field" style={{ marginBottom: "14px" }}>
                <span className="sp-field-label">Warmth (temperature)</span>
                <div className="sp-warmth-row">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    data-testid="prompt-temperature-range"
                    value={form.temperature}
                    onChange={updateField("temperature")}
                    disabled={isViewingExisting}
                  />
                  <span className="sp-warmth-readout">{Number(form.temperature).toFixed(1)}</span>
                </div>
                <span className="sp-hint">
                  Lower stays close to the transcript; higher allows more interpretation.
                </span>
              </div>

              <div className="sp-grid-2" style={{ alignItems: "start" }}>
                <div className="sp-field">
                  <span className="sp-field-label">Prompt text</span>
                  <textarea
                    className="sp-control sp-prompt-textarea"
                    data-testid="prompt-text-input"
                    rows={12}
                    value={form.promptText}
                    onChange={updateField("promptText")}
                    disabled={isViewingExisting}
                    placeholder="Read this tutoring session transcript. For each student utterance, decide whether it demonstrates a collaboration skill…"
                  />
                </div>

                <div className="sp-field">
                  <span className="sp-field-label">Active skill vocabulary</span>
                  <div className="sp-vocab-box" data-testid="vocabulary-box">
                    {!vocabulary || vocabulary.tags.length === 0 ? (
                      <span className="sp-hint">No active vocabulary defined yet.</span>
                    ) : (
                      vocabulary.tags.map((t) => (
                        <div key={t.id} className="sp-vocab-row">
                          <span className="sp-vocab-tag">
                            {t.skill}: {t.tag}
                          </span>
                          {t.description && <span className="sp-vocab-desc"> — {t.description}</span>}
                        </div>
                      ))
                    )}
                  </div>

                  <span className="sp-field-label" style={{ marginTop: "14px", display: "block" }}>
                    JSON output contract
                  </span>
                  <pre className="sp-contract-box" data-testid="output-contract-box">
                    {OUTPUT_CONTRACT_EXAMPLE}
                  </pre>
                </div>
              </div>

              <div style={{ marginTop: "16px" }}>
                {isViewingExisting ? (
                  <span className="sp-hint">
                    Viewing a saved prompt. Choose "+ New prompt" to define another.
                  </span>
                ) : (
                  <button
                    type="submit"
                    className="sp-btn sp-btn-primary"
                    data-testid="save-prompt-btn"
                    disabled={savingPrompt || !form.name.trim() || !form.promptText.trim()}
                  >
                    {savingPrompt ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Saving…
                      </>
                    ) : (
                      "Save prompt"
                    )}
                  </button>
                )}
              </div>
            </form>

            <form className="sp-card" style={{ marginTop: "18px" }} onSubmit={handleRunPrompt}>
              <div className="sp-card-title">Run against batches</div>

              {!selectedPrompt ? (
                <span className="sp-hint">Select or save a prompt above to run it.</span>
              ) : (
                <>
                  <div className="sp-field" style={{ marginBottom: "14px" }}>
                    <span className="sp-field-label">Prompt</span>
                    <span data-testid="selected-prompt-name" style={{ fontWeight: 600 }}>
                      {selectedPrompt.name}
                    </span>
                  </div>

                  <div className="sp-field" style={{ marginBottom: "14px" }}>
                    <span className="sp-field-label">Batches</span>
                    <div className="sp-batch-checks" data-testid="batch-checks">
                      {batches.length === 0 ? (
                        <span className="sp-hint">No batches available.</span>
                      ) : (
                        batches.map((b) => (
                          <label key={b.id}>
                            <input
                              type="checkbox"
                              data-testid={`batch-check-${b.id}`}
                              checked={selectedBatchIds.includes(b.id)}
                              onChange={() => toggleBatch(b.id)}
                            />
                            {b.name} ({b.session_count || 0} sessions)
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="sp-field" style={{ marginBottom: "16px", maxWidth: "220px" }}>
                    <span className="sp-field-label">Cost ceiling (USD)</span>
                    <input
                      type="number"
                      className="sp-control"
                      data-testid="cost-ceiling-input"
                      min="0.01"
                      max={DEFAULT_COST_CEILING_USD}
                      step="0.01"
                      value={costCeiling}
                      onChange={(e) => setCostCeiling(e.target.value)}
                    />
                    <span className="sp-hint">
                      Default ${DEFAULT_COST_CEILING_USD.toFixed(2)}, only lowerable here. The backend hard-caps every
                      run at ${MAX_COST_CEILING_USD.toFixed(2)} regardless.
                    </span>
                  </div>

                  <button
                    type="submit"
                    className="sp-btn sp-btn-primary"
                    data-testid="run-prompt-btn"
                    disabled={triggering || selectedBatchIds.length === 0 || !costCeiling || Number(costCeiling) <= 0}
                  >
                    {triggering ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Triggering…
                      </>
                    ) : (
                      "Run prompt"
                    )}
                  </button>
                </>
              )}
            </form>

            <div className="sp-card" style={{ marginTop: "18px" }}>
              <div className="sp-card-title">Run history</div>
              {runs.length === 0 ? (
                <div className="sp-hint" data-testid="runs-empty">
                  No runs yet.
                </div>
              ) : (
                <table className="sp-table" data-testid="runs-table">
                  <thead>
                    <tr>
                      <th>Prompt</th>
                      <th>Batches</th>
                      <th>Status</th>
                      <th>Cost</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run) => {
                      const prompt = promptMap.get(run.prompt_id);
                      const batchNames = (run.batch_ids || [])
                        .map((id) => batchMap.get(id)?.name || id)
                        .join(", ");
                      return (
                        <tr key={run.id} data-testid={`run-row-${run.id}`}>
                          <td>{prompt?.name || "Unknown prompt"}</td>
                          <td>{batchNames || "—"}</td>
                          <td>
                            {renderStatus(run)}
                            {(run.status === "failed" || run.status === "cost_exceeded") && run.error && (
                              <div className="sp-run-error" data-testid={`run-error-${run.id}`}>
                                {run.error}
                              </div>
                            )}
                          </td>
                          <td>{run.cost_usd != null ? `$${run.cost_usd.toFixed(2)}` : "—"}</td>
                          <td>{formatDate(run.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Exported for tests only.
export const __testables = { formatDate, modelsForProvider };
